import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "./TopbarLayout.scss";
import { Header } from "antd/lib/layout/layout";
import { productColors } from "../../assets/variables";
import Logotype from "../Logotype/Logotype";
import { useEffect } from "react";
import useTailwindResponsive from "../../hooks/tailwindResponsive.hook";
import ScrollOverlay from "../Overflow/ScrollOverlay.component";
import useSlot from "../../hooks/slot.hook";
import useTopbarLayoutStore from "./_store/TopbarLayout.store";
import IconActionWrapper from "./_components/IconActionWrapper.component";
/**
 * A flexible navigation bar on top of the page.
 * Use this component instead of the less flexible `TopBar` component.
 * Simply pass slots of the top bar as children.
 * Common parts are `TopBarContent.ProductSelector`,
 * `TopBarContent.OrganizationName`, `TopBarContent.EnvTag`,
 * `TopBarContent.NavigationMenu`, and `TopBarContent.UserDropdown`.
 *
 * Prop `currentProduct` is only used to theme the top bar.
 * You should still pass the current product to the relevant parts (e.g. `ProductSelector`).
 */
export const TopBarLayout = ({ currentProduct, showTimeEditIcon = true, parts, slots = parts }) => {
    const { gap: responsiveGap } = useTailwindResponsive();
    const { setCenterItems, setCurrentProduct, setShowTimeEditIcon } = useTopbarLayoutStore();
    // Check if the HamburgerMenu is used in the right slots
    const HamburgerMenu = useSlot({
        displayName: "HamburgerMenu",
        element: slots === null || slots === void 0 ? void 0 : slots.right
    });
    /**
     * Add center items to the store if we have a hamburger menu, this will be used as the `default` navigation items
     */
    useEffect(() => {
        if (!(slots === null || slots === void 0 ? void 0 : slots.center))
            return;
        if (!HamburgerMenu)
            return;
        if (HamburgerMenu.props.children)
            return;
        setCenterItems(slots.center);
    }, [HamburgerMenu, slots]);
    /**
     * Add current product to the store, used to style the background overlay color for the hamburger menu
     */
    useEffect(() => {
        if (!HamburgerMenu)
            return;
        if (!currentProduct)
            return;
        setCurrentProduct(currentProduct);
    }, [HamburgerMenu, currentProduct]);
    /**
     * Add showTimeEditIcon to the store, we need this to make it visible in the HamburgerMenu
     */
    useEffect(() => {
        if (!HamburgerMenu)
            return;
        setShowTimeEditIcon(showTimeEditIcon);
    }, [HamburgerMenu, showTimeEditIcon]);
    return (_jsx(Header, { "data-testid": "top-bar-wrapper", className: `
        top-bar-layout-wrapper
        te-p-0 te-leading-[revert] !te-bg-te-colorBgBase !te-h-[51px]
        te-shadow-sm te-flex te-w-full te-overflow-hidden
      `, style: { borderBottom: `3px solid ${productColors[currentProduct]}` }, children: _jsxs("div", { className: `
          te-flex te-items-center te-w-full  te-overflow-x-auto te-overflow-y-hidden
          sm:gap-2
        `, children: [_jsx("div", { className: `
            te-flex te-h-full te-items-center sm:te-min-w-fit te-pl-2
            ${responsiveGap}
          `, children: slots === null || slots === void 0 ? void 0 : slots.left }), _jsx("div", { className: `
              te-grow te-shrink te-min-w-0 te-h-full te-flex
              te-bg-colorBgBase
            `, children: _jsx("div", { className: `te-h-full te-grow te-shrink te-min-w-0 te-gap-2 sm:te-gap-4 ${HamburgerMenu ? "te-hidden sm:te-flex" : "te-flex"}`, children: slots === null || slots === void 0 ? void 0 : slots.center }) }), _jsxs("div", { className: `
            te-flex te-h-full te-items-center te-shrink-0 te-w-fit te-right-0 te-sticky
          `, children: [_jsx(ScrollOverlay, { direction: "horizontal", className: "sm:te-hidden" }), _jsxs("div", { className: `
              te-bg-te-colorBgBase te-flex te-h-full te-items-center te-pl-2
              ${!showTimeEditIcon && !HamburgerMenu ? "te-pr-2" : ""}
            `, children: [slots === null || slots === void 0 ? void 0 : slots.right, showTimeEditIcon && !HamburgerMenu && (_jsx(IconActionWrapper, { children: _jsx(Logotype, { alt: "TimeEdit Logo", id: "TE", size: 24 }) }))] })] })] }) }));
};
